// ── Inventory math — the ONE place rep stock is computed ───────────────────────
//
// PURE module: no React, no Supabase, no localStorage, no Date.now(), no console.
// Every screen (rep portal "My inventory", admin "Rep Inventory", delete-impact,
// diagnostics) must derive received / sold / on-hand from these functions so the
// numbers cannot diverge between accounts.
//
// THE MODEL (owner decisions, final)
//   received(rep, sku) = SIGNED sum of that rep's ledger rows (inventory_movements)
//                        of type assign / return / adjust_rep / rep_receive.
//                        return and negative adjustments are stored negative.
//                        warehouse_* rows are never rep stock.
//   sold(rep, sku)     = UNITS on every invoice that "deducts stock from this rep":
//                          - invoices the REP created (INV-F… / repCreated /
//                            source rep_invoice), and
//                          - dealer-portal orders whose deliveryMethod is "rep"
//                            (the rep delivers them himself; the rep named in
//                            deliveredByRep is the one whose stock is used).
//                        Dealer orders shipped by carrier / notes_only / MPC never
//                        touch rep stock. Cancelled / voided / removed invoices
//                        never deduct, so a void RESTORES the units automatically.
//                        NO ledger row is ever written for a sale, void or cancel.
//   onHand             = received - sold. SIGNED, never clamped. A negative number
//                        means the rep is oversold (sold more than was received).
//
// The period filter (`since`) only produces a separate display figure
// (soldInPeriod). It never changes onHand.
//
// Attribution of an invoice to a rep: an invoice-level rep stamp (inv.rep / inv.repId /
// inv.repEmail) is AUTHORITATIVE when present, and a customer-name match is used ONLY
// for invoices that carry no usable stamp (empty, or a generic name such as "Sales
// Rep"). One invoice is NEVER deducted from two reps:
//   - a stamp id (repId) equal to the rep id wins;
//   - a NAME-only stamp (what portal_orders carries) counts, in the rep portal, only on
//     exact cleaned full-name equality with the rep's name / alias / email;
//   - the admin tab and the delete-impact figure know every rep, so they resolve a
//     name-only stamp to ONE owner (createOwnerResolver): exact name wins, the fuzzy
//     first-name + last-initial match counts only if exactly one listed rep matches,
//     several matches and no exact one leave the invoice UNATTRIBUTED (nobody's).
// This is also the fix for the PR #79 double-attribution bug.
//
// Nothing here throws on malformed input: bad rows are skipped or count as 0.

import { repNamesMatch, parseInvoiceDate, collectRepCustomerNames } from "./repMetrics";

/** Movement types that make up a rep's personal stock. warehouse_receive /
 *  warehouse_adjust describe the warehouse counter, never a rep. */
export const REP_LEDGER_TYPES = Object.freeze(["assign", "return", "adjust_rep", "rep_receive"]);

/** Invoice statuses that never deduct stock (void / cancel restores the units). */
const NON_DEDUCTING_STATUSES = new Set(["cancelled", "canceled", "removed"]);

// ── small safe helpers ─────────────────────────────────────────────────────────

/** Finite number from a number or numeric string; null for everything else
 *  (null, undefined, "", booleans, arrays, NaN, Infinity). */
function toNum(v) {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** Trimmed lower-case string; "" for null/undefined/non-scalars. */
function low(s) {
  if (s == null || typeof s === "object") return "";
  return String(s).trim().toLowerCase();
}

/** Round to a whole non-negative unit count. */
function wholeUnits(n) {
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.round(n);
}

/** True when `sku` is empty or one of the placeholder dashes the dealer cart
 *  stores for a line with no SKU. Those lines cannot be keyed to a product. */
function isBlankSku(sku) {
  const k = normSku(sku);
  return k === "" || k === "—" || k === "-" || k === "–";
}

// ── SKU identity ───────────────────────────────────────────────────────────────

/** Canonical grouping key for a SKU: trimmed + upper-cased string ("" if null).
 *  "7000", " 7000 " and "7000" collapse to one key. Use the catalog sku or the
 *  first-seen original spelling for DISPLAY; use this only for grouping. */
export function normSku(s) {
  if (s == null || typeof s === "object") return "";
  return String(s).trim().toUpperCase();
}

// ── invoice classification ─────────────────────────────────────────────────────

/** True for an invoice the REP created in the field: id starts with INV-F, or the
 *  repCreated flag, or source === "rep_invoice". Dealer-portal orders are anything
 *  else. (qty on a rep-created invoice is already in UNITS.) */
export function isRepCreatedInvoice(inv) {
  if (!inv || typeof inv !== "object") return false;
  if (inv.repCreated) return true;
  if (String(inv.id == null ? "" : inv.id).toUpperCase().startsWith("INV-F")) return true;
  return low(inv.source) === "rep_invoice";
}

/** Status test: cancelled / canceled / removed never deduct. */
export function isNonDeductingStatus(inv) {
  return !!inv && NON_DEDUCTING_STATUSES.has(low(inv.status));
}

/** The invoice's line items as an array of objects. Accepts an array or a JSON
 *  string (portal_orders.items is TEXT); anything else is []. null lines dropped. */
export function invoiceItems(inv) {
  let items = inv && inv.items;
  if (typeof items === "string") {
    try { items = JSON.parse(items); } catch (e) { return []; }
  }
  if (!Array.isArray(items)) return [];
  return items.filter((it) => it && typeof it === "object");
}

// ── rep identity + attribution ─────────────────────────────────────────────────

/** Rep stamps that carry NO information ("Sales Rep" is the fallback name the rep
 *  portal writes when it does not know the rep's name). An invoice whose only stamp
 *  is one of these (or empty) is UNSTAMPED. */
const GENERIC_STAMP_NAMES = new Set(["sales rep", "rep", "unknown", "-", "—", "–"]);

/** Cleaned comparison form of a name: lower-case, periods dropped, whitespace
 *  collapsed ("Sam T." -> "sam t", "Sam  Tarabay" -> "sam tarabay"). */
export function cleanStampName(s) {
  if (s == null || typeof s === "object") return "";
  return String(s).toLowerCase().replace(/\./g, "").replace(/\s+/g, " ").trim();
}

function isGenericStampName(c) { return c === "" || GENERIC_STAMP_NAMES.has(c); }

/** Normalize a rep identity. Input (all optional):
 *    { id, name, email, aliases: [names/emails, any case], customerNames: [] }
 *  Output: { prepared, id, name, emails:Set, names:string[], cnames:string[], customers:Set }
 *  (lower-cased; cnames are the cleaned names used for exact comparison). Passing an
 *  already-prepared identity returns it unchanged, so loops over many invoices can
 *  prepare once. There is no hidden cache, so mutating the input afterwards is safe. */
export function prepareRep(rep) {
  const r = rep && typeof rep === "object" ? rep : {};
  if (r.prepared === true) return r;
  const emails = new Set();
  const names = [];
  const add = (v) => {
    const s = low(v);
    if (!s) return;
    if (s.includes("@")) emails.add(s);
    else if (!names.includes(s)) names.push(s);
  };
  add(r.name);
  add(r.email);
  (Array.isArray(r.aliases) ? r.aliases : []).forEach(add);
  const cnames = [];
  names.forEach((n) => { const c = cleanStampName(n); if (c && !cnames.includes(c)) cnames.push(c); });
  const customers = new Set();
  (Array.isArray(r.customerNames) ? r.customerNames : []).forEach((c) => { const s = low(c); if (s) customers.add(s); });
  return {
    prepared: true,
    id: r.id == null || r.id === "" ? null : String(r.id),
    name: low(r.name),
    emails,
    names,
    cnames,
    customers,
  };
}

/** The invoice's rep stamp, normalized: { id, email, name } where name is the CLEANED
 *  stamp name ("" when the stamp is empty or generic) and an email typed into the
 *  name field counts as an email. */
function stampOf(inv) {
  const id = inv.repId == null || inv.repId === "" ? null : String(inv.repId);
  let email = low(inv.repEmail || inv.rep_email);
  let name = cleanStampName(inv.rep);
  // an email typed into the name field is an email (compared with its dots intact)
  if (low(inv.rep).includes("@")) { if (!email) email = low(inv.rep); name = ""; }
  if (isGenericStampName(name)) name = "";
  return { id, email, name };
}

/** True when the invoice carries no usable rep stamp at all (no id, no email, and a
 *  name that is empty or generic such as "Sales Rep"). Such an invoice falls through
 *  to the customer-list rule. */
export function isUnstampedInvoice(inv) {
  if (!inv || typeof inv !== "object") return false;
  const s = stampOf(inv);
  return !s.id && !s.email && !s.name;
}

/** Dealer-portal order rule: only deliveryMethod "rep" (case-insensitive) takes
 *  stock from a rep. carrier / notes_only / empty / unknown never do. */
function dealerOrderDeliveredByRep(inv) {
  return low(inv && inv.deliveryMethod) === "rep";
}

/** The stamp that decides WHOSE stock an invoice uses. For a dealer order delivered
 *  by a rep the rep who DELIVERED it (inv.deliveredByRep, picked by admin in the Ship
 *  modal, a NAME only) decides; an empty or generic deliveredByRep is ignored and the
 *  normal rep stamp applies. */
function stampForOwnership(inv) {
  return deliveredByStamp(inv) || stampOf(inv);
}

/** The delivering-rep stamp of a dealer order delivered by a rep (null when the invoice
 *  is not one, or deliveredByRep is empty / generic). A NAME (or an email) only. */
function deliveredByStamp(inv) {
  if (!isRepCreatedInvoice(inv) && dealerOrderDeliveredByRep(inv)) {
    const byRaw = low(inv.deliveredByRep);
    const by = cleanStampName(inv.deliveredByRep);
    if (!isGenericStampName(by)) return byRaw.includes("@") ? { id: null, email: byRaw, name: "" } : { id: null, email: "", name: by };
  }
  return null;
}

/** Indexes of the prepared reps in `peers` that a stamp names.
 *   - unstamped: the reps whose customer list holds inv.customer;
 *   - a stamp id decides among reps that have an id (equal -> that rep); only when no
 *     rep has that id may the reps WITHOUT an id be matched by email / name;
 *   - an email equal to a rep email wins;
 *   - a NAME matches by exact cleaned equality (rep name, alias or email); the fuzzy
 *     first-name + last-initial matcher is used only when `fuzzy` is true AND no exact
 *     match exists.
 *  The caller decides what a result with 0 or several indexes means. */
function ownersForStamp(st, peers, customer, fuzzy) {
  const all = peers.map((_, i) => i);
  if (!st.id && !st.email && !st.name) {
    const c = low(customer);
    return c ? all.filter((i) => peers[i].customers.has(c)) : [];
  }
  let pool = all;
  if (st.id) {
    const byId = pool.filter((i) => peers[i].id && peers[i].id === st.id);
    if (byId.length) return byId;
    pool = pool.filter((i) => !peers[i].id);
  }
  if (st.email) {
    const byEmail = pool.filter((i) => peers[i].emails.has(st.email));
    if (byEmail.length) return byEmail;
  }
  if (st.name) {
    const exact = pool.filter((i) => peers[i].cnames.includes(st.name));
    if (exact.length || !fuzzy) return exact;
    return pool.filter((i) => peers[i].cnames.some((n) => repNamesMatch(st.name, n)));
  }
  return [];
}

/** Resolve every invoice to ONE owner among a whole rep list (the admin tab and the
 *  delete-impact figure know every rep; the rep portal knows only itself and does not
 *  use this). Rule: an exact cleaned full-name match (name, alias or email) wins; the
 *  fuzzy first-name + last-initial match applies only if EXACTLY ONE listed rep
 *  matches; several matches and no exact one -> the invoice is UNATTRIBUTED (deducted
 *  from nobody). An unstamped invoice (empty / "Sales Rep") is resolved through the
 *  customer lists the same way (exactly one rep holds the customer).
 *  @param {Array} repList  rep identities ({id, name, email, aliases, customerNames})
 *  @returns {{peers:Array, source:Array, resolve:(inv)=>{index:number, reason:string, candidates:number[]}, indexOfRep:(rep)=>number}}
 *     reason: "owner" | "ambiguous" | "none". */
export function createOwnerResolver(repList) {
  const source = Array.isArray(repList) ? repList : [];
  const peers = source.map(prepareRep);
  const memo = new WeakMap();
  const resolve = (inv) => {
    if (!inv || typeof inv !== "object") return { index: -1, reason: "none", candidates: [] };
    const hit = memo.get(inv);
    if (hit) return hit;
    // A dealer order delivered by a rep: the delivering rep's NAME decides, but when that
    // name yields no single owner (a short or unknown name) the account rep stamp
    // (inv.rep) is tried before the invoice is declared unattributed.
    const by = deliveredByStamp(inv);
    let idxs = ownersForStamp(by || stampOf(inv), peers, inv.customer, true);
    let deliveredByCause = false;
    if (by && idxs.length !== 1) {
      const alt = ownersForStamp(stampOf(inv), peers, inv.customer, true);
      if (alt.length === 1) idxs = alt;
      else {
        deliveredByCause = true;
        if (idxs.length <= 1 && alt.length > 1) idxs = alt;     // report the stamp that was ambiguous
      }
    }
    const out = idxs.length === 1
      ? { index: idxs[0], reason: "owner", candidates: idxs, deliveredByCause: false }
      : { index: -1, reason: idxs.length > 1 ? "ambiguous" : "none", candidates: idxs, deliveredByCause };
    memo.set(inv, out);
    return out;
  };
  const indexOfRep = (rep) => {
    const p = prepareRep(rep);
    if (p.id) return peers.findIndex((q) => q.id === p.id);
    return p.name ? peers.findIndex((q) => !q.id && q.name === p.name) : -1;
  };
  return { peers, source, resolve, indexOfRep };
}

/** Which listed rep (the original object from `repList`) owns this invoice, or null.
 *  Convenience wrapper over createOwnerResolver for one-off questions. */
export function resolveStampOwner(inv, repList) {
  const r = createOwnerResolver(repList);
  const res = r.resolve(inv);
  return { rep: res.index >= 0 ? r.source[res.index] : null, reason: res.reason, candidates: res.candidates.map((i) => r.source[i]) };
}

/** Is the invoice one of the rep it names? Without `ctx.resolver` (the rep portal, a
 *  lone identity) a name-only stamp counts ONLY on exact cleaned full-name equality
 *  with the rep's name / alias / email, never fuzzy. With `ctx.resolver` the invoice
 *  counts only when the resolver picked THIS rep as its single owner. */
function ownedByRep(inv, p, ctx) {
  if (ctx && ctx.resolver) {
    const i = ctx.resolver.indexOfRep(p);
    if (i >= 0) return ctx.resolver.resolve(inv).index === i;
  }
  return ownersForStamp(stampForOwnership(inv), [p], inv.customer, false).length === 1;
}

/** Attribution only (no source / status rules): is this invoice "this rep's"?
 *  Precedence (mirrors repMetrics.filterRepInvoices, minus its fuzzy name match):
 *   1. If the invoice carries ANY real rep stamp (inv.rep, inv.repId, inv.repEmail)
 *      the stamp decides and the customer list is never consulted:
 *        - repId and rep.id both known: equal -> true, different -> false
 *          (a stable id naming another rep wins over a name);
 *        - else repEmail equality, else the cleaned-name equality.
 *   2. An UNSTAMPED invoice (nothing, or only a generic name such as "Sales Rep") is
 *      attributed through rep.customerNames.
 *  @param {object} [ctx] { resolver } from createOwnerResolver(repList) for list mode */
export function invoiceAttributedToRep(inv, rep, ctx) {
  if (!inv || typeof inv !== "object") return false;
  return ownedByRep(inv, prepareRep(rep), ctx);
}

/** Could this invoice take stock out of SOME rep (not cancelled, and either created
 *  by a rep or a dealer order delivered by a rep)? Says nothing about WHICH rep. */
export function invoiceCanDeductStock(inv) {
  if (!inv || typeof inv !== "object") return false;
  if (isNonDeductingStatus(inv)) return false;
  return isRepCreatedInvoice(inv) || dealerOrderDeliveredByRep(inv);
}

/** THE single rule for "does this invoice take stock out of rep R".
 *  Order of checks: cancelled/removed -> false; neither a rep-created invoice nor a
 *  dealer order delivered by a rep -> false; otherwise the invoice must be owned by R
 *  (see ownedByRep). For a dealer order delivered by a rep, the delivering rep
 *  (inv.deliveredByRep) decides; only when it is empty / generic does the normal rep
 *  stamp / customer rule apply. Never throws.
 *  @param {object} [ctx] { resolver } for list mode (admin, delete-impact) */
export function invoiceDeductsRepStock(inv, rep, ctx) {
  if (!invoiceCanDeductStock(inv)) return false;
  return ownedByRep(inv, prepareRep(rep), ctx);
}

/** Invoices that would take stock out of a rep but that no listed rep owns EXACTLY
 *  ONE of: "ambiguous" (several reps match the stamp and none exactly) or "none"
 *  (nobody matches). They are deducted from nobody; the admin tab lists them so the
 *  figure is never silently short.
 *  @param {Array} invoices
 *  @param {Array} repList  rep identities
 *  @param {{resolver?:object, caseBySku?:Object}} [opts]
 *  @returns {{count:number, units:number, ambiguous:number, unmatched:number,
 *             items:Array<{id,rep,customer,units,reason,candidates:string[]}>}} */
export function unattributedInvoices(invoices, repList, opts) {
  const resolver = (opts && opts.resolver) || createOwnerResolver(repList);
  const out = { count: 0, units: 0, ambiguous: 0, unmatched: 0, items: [] };
  dedupeInvoicesById(invoices).forEach((inv) => {
    if (!invoiceCanDeductStock(inv)) return;
    const r = resolver.resolve(inv);
    if (r.index >= 0) return;
    const bySku = {};
    const units = invoiceItems(inv).reduce((s, it) => {
      const u = lineUnits(it, inv, opts);
      const k = normSku(it.sku);
      if (k && u > 0) bySku[k] = (bySku[k] || 0) + u;
      return s + u;
    }, 0);
    out.count += 1;
    out.units += units;
    if (r.reason === "ambiguous") out.ambiguous += 1; else out.unmatched += 1;
    out.items.push({
      id: inv.id == null ? "" : String(inv.id),
      rep: String(inv.deliveredByRep || inv.rep || ""),
      accountRep: String(inv.rep || ""),
      deliveredBy: r.deliveredByCause ? String(inv.deliveredByRep || "") : "",
      deliveredByCause: !!r.deliveredByCause,
      customer: String(inv.customer || ""),
      units,
      bySku,
      reason: r.reason,
      candidates: r.candidates.map((i) => String((resolver.source[i] && resolver.source[i].name) || "")),
    });
  });
  return out;
}

/** The rep identity every screen feeds to inventoryMath, built in ONE place so the
 *  Rep Inventory tab, the Adjust baseline and the delete-impact figure cannot differ.
 *  Aliases: the display names stored in SHARED_REP_PROFILES under the rep's key /
 *  email / supabase id, and every rep_name admin snapshotted on that rep's ledger
 *  rows. Customer list: admin-assigned accounts + the rep's route customers.
 *  @param {{rep:{key,name,email,supabaseId}, ledgerRows?:Array, sharedRepProfiles?:Object,
 *           sharedRepCustomers?:Object, assignedAccounts?:Array}} args */
export function buildRepIdentity(args) {
  const a = args || {};
  const r = a.rep || {};
  const profiles = a.sharedRepProfiles || {};
  const aliases = [];
  [r.key, r.email, r.supabaseId].forEach((k) => {
    const sp = k && profiles[k];
    if (sp && sp.name) aliases.push(sp.name);
  });
  (Array.isArray(a.ledgerRows) ? a.ledgerRows : []).forEach((m) => {
    if (m && r.key != null && String(m.rep_id) === String(r.key) && m.rep_name) aliases.push(m.rep_name);
  });
  const routes = a.sharedRepCustomers && r.name != null ? a.sharedRepCustomers[r.name] : null;
  return {
    id: r.key,
    name: r.name,
    email: r.email || "",
    aliases,
    customerNames: collectRepCustomerNames({
      repName: r.name,
      assignedAccounts: a.assignedAccounts || [],
      sharedRepCustomers: routes || null,
      staticCustomers: [],
    }),
  };
}

/** Collapse duplicate invoice ids (a cache copy + a hydrated copy of the same
 *  invoice) to ONE copy per id, keeping the NEWEST (largest sharedAt / createdAt /
 *  parsed date). Ties keep the first copy seen. Invoices without an id are kept
 *  as they are. Order of the survivors follows first appearance. Pure. */
export function dedupeInvoicesById(invoices) {
  const stamp = (inv) => {
    const t = Date.parse(inv && (inv.sharedAt || inv.createdAt) ? (inv.sharedAt || inv.createdAt) : "");
    if (Number.isFinite(t)) return t;
    const d = parseInvoiceDate(inv);
    return d ? d.getTime() : -Infinity;
  };
  const out = [];
  const at = new Map();
  (Array.isArray(invoices) ? invoices : []).forEach((inv) => {
    if (!inv || typeof inv !== "object") return;
    const id = inv.id == null || inv.id === "" ? null : String(inv.id);
    if (id == null) { out.push(inv); return; }
    if (!at.has(id)) { at.set(id, out.length); out.push(inv); return; }
    const i = at.get(id);
    const keep = stamp(inv) > stamp(out[i]) ? inv : out[i];
    const other = keep === inv ? out[i] : inv;
    // Cancellation is TERMINAL across duplicate copies too: a newer stale copy must not
    // revive an invoice another copy of the same id shows as cancelled / removed.
    if (!NON_DEDUCTING_STATUSES.has(low(keep.status)) && NON_DEDUCTING_STATUSES.has(low(other.status))) {
      out[i] = { ...keep, status: other.status };
    } else {
      out[i] = keep;
    }
  });
  return out;
}

// ── units per line ─────────────────────────────────────────────────────────────

/** Units for ONE invoice line (never negative, never NaN, whole number).
 *  Storage shapes (see the investigation report):
 *   - rep-created invoice: item.qty IS units (price is per unit).
 *   - dealer order, current shapes: item.qty is CASES / half-cases / mix sets and
 *     item.units is the real unit count -> use item.units
 *       full:      units = case * cases        half: units = ceil(case/2) * qty
 *       mix:       units = sum of the mix      variation / kit: units = case * cases
 *   - dealer order without item.units (legacy, before 2026-04-19, or malformed):
 *       half -> ceil(case/2) * qty; mix with mixItems -> sum(mix qty) * sets;
 *       otherwise qty * case, where case is item.case, else opts.caseBySku[normSku],
 *       else 1 (legacy lines carry no case, so this is usually plain qty).
 *  @param {object} item
 *  @param {object} inv   decides rep-created vs dealer order
 *  @param {{caseBySku?:Object<string,number>}} [opts] */
export function lineUnits(item, inv, opts) {
  if (!item || typeof item !== "object") return 0;
  const qty = toNum(item.qty);
  if (isRepCreatedInvoice(inv)) return wholeUnits(qty == null ? 0 : qty);

  const units = toNum(item.units);
  if (units != null) return wholeUnits(units);

  const mode = low(item.mode);
  const n = qty == null ? 0 : qty;
  let cs = toNum(item.case);
  if (cs == null || cs < 1) {
    const fromCatalog = opts && opts.caseBySku ? toNum(opts.caseBySku[normSku(item.sku)]) : null;
    cs = fromCatalog != null && fromCatalog >= 1 ? fromCatalog : 1;
  }
  if (mode === "half") return wholeUnits(Math.ceil(cs / 2) * n);
  if (mode === "mix" && Array.isArray(item.mixItems)) {
    const per = item.mixItems.reduce((s, r) => s + (toNum(r && r.qty) || 0), 0);
    return wholeUnits(per * (n || 1));
  }
  return wholeUnits(n * cs);
}

// ── ledger side ────────────────────────────────────────────────────────────────

/** Does this ledger row belong to the rep? By id when the rep id is known,
 *  otherwise by exact (lower-cased) rep_name against the rep's names. A row with
 *  a null rep_id is never a rep's row when a specific rep is asked for. */
function movementBelongsToRep(m, p) {
  if (m.rep_id == null || m.rep_id === "") return false;
  if (p.id) return String(m.rep_id) === p.id;
  const nm = low(m.rep_name);
  return !!nm && p.names.includes(nm);
}

/** Signed received units per SKU from ledger rows.
 *  @param {Array} movements  inventory_movements rows (any reps, any types)
 *  @param {{types?:string[], rep?:object}} [opts]
 *     types: movement types to count (default REP_LEDGER_TYPES);
 *     rep:   when given, only that rep's rows count (see movementBelongsToRep);
 *            when omitted, every row with a non-null rep_id of a counted type.
 *  @returns {{bySku:Object, total:number, count:number, skipped:number}}
 *     bySku[KEY] = { key, sku, name, received, byType:{type:sum}, count }.
 *     skipped = rows ignored for a bad qty / no sku. */
export function ledgerReceivedBySku(movements, opts) {
  const types = new Set((opts && Array.isArray(opts.types) ? opts.types : REP_LEDGER_TYPES).map((t) => low(t)));
  const p = opts && opts.rep ? prepareRep(opts.rep) : null;
  const out = { bySku: {}, total: 0, count: 0, skipped: 0 };
  (Array.isArray(movements) ? movements : []).forEach((m) => {
    if (!m || typeof m !== "object") return;
    const type = low(m.movement_type);
    if (!types.has(type)) return;
    if (p ? !movementBelongsToRep(m, p) : (m.rep_id == null || m.rep_id === "")) return;
    const key = normSku(m.sku);
    const q = toNum(m.qty);
    if (!key || q == null) { out.skipped += 1; return; }
    const e = out.bySku[key] || (out.bySku[key] = {
      key, sku: String(m.sku).trim(), name: m.product_name ? String(m.product_name) : "", received: 0, byType: {}, count: 0,
    });
    if (!e.name && m.product_name) e.name = String(m.product_name);
    e.received += q;
    e.byType[type] = (e.byType[type] || 0) + q;
    e.count += 1;
    out.total += q;
    out.count += 1;
  });
  return out;
}

// ── sold side ──────────────────────────────────────────────────────────────────

/** Normalize the `since` option to a Date (or null when absent/invalid). */
function toSince(since) {
  if (since == null || since === "") return null;
  const d = since instanceof Date ? since : new Date(since);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Is the invoice dated on/after `since`? Unparseable dates are NOT in the period
 *  (same as the existing admin period filter). */
function inPeriod(inv, since) {
  const d = parseInvoiceDate(inv);
  return !!d && d >= since;
}

/** Units sold per SKU by invoices that deduct stock from `rep`.
 *  @param {Array} invoices
 *  @param {object} rep   { id, name, email, aliases, customerNames }
 *  @param {{since?:string|Date, caseBySku?:Object, resolver?:object}} [opts]
 *     since applies ONLY to the unitsInPeriod display figure; `units` is all-time.
 *     resolver (createOwnerResolver(repList)) switches name-only stamps to the
 *     one-owner rule across the whole rep list (admin); without it a name-only stamp
 *     counts only on exact cleaned-name equality (rep portal).
 *  @returns {{bySku:Object, total:number, invoiceCount:number, unkeyedUnits:number}}
 *     bySku[KEY] = { key, sku, name, units, unitsInPeriod }.
 *     unitsInPeriod === units when no (valid) `since` is given.
 *     unkeyedUnits = units on lines with no usable SKU (cannot be placed on a row). */
export function soldBySku(invoices, rep, opts) {
  const since = toSince(opts && opts.since);
  const out = { bySku: {}, total: 0, invoiceCount: 0, unkeyedUnits: 0 };
  const seen = new Set();
  const pr = prepareRep(rep);
  const ctx = { resolver: opts && opts.resolver };
  (Array.isArray(invoices) ? invoices : []).forEach((inv) => {
    if (!invoiceDeductsRepStock(inv, pr, ctx)) return;
    // A second copy of the same invoice id (cache + hydrate) must not deduct twice.
    const id = inv.id == null || inv.id === "" ? null : String(inv.id);
    if (id) { if (seen.has(id)) return; seen.add(id); }
    out.invoiceCount += 1;
    const counted = since ? inPeriod(inv, since) : true;
    invoiceItems(inv).forEach((it) => {
      const u = lineUnits(it, inv, opts);
      if (u <= 0) return;
      if (isBlankSku(it.sku)) { out.unkeyedUnits += u; return; }
      const key = normSku(it.sku);
      const e = out.bySku[key] || (out.bySku[key] = {
        key, sku: String(it.sku).trim(), name: it.name ? String(it.name) : "", units: 0, unitsInPeriod: 0,
      });
      if (!e.name && it.name) e.name = String(it.name);
      e.units += u;
      if (counted) e.unitsInPeriod += u;
      out.total += u;
    });
  });
  return out;
}

// ── rows + totals ──────────────────────────────────────────────────────────────

/** Index a catalog (products, kits, and variation SKUs) by normSku.
 *  Accepts an array of { sku, name, case, price, isKit, variations:[{sku,label,name,price}] }.
 *  price is the per-unit catalog price (0 when unknown); isKit marks kit lines.
 *  @returns {Object<string,{sku:string,name:string,case:number|null,price:number,isKit:boolean}>} */
export function indexCatalog(catalog) {
  const idx = {};
  (Array.isArray(catalog) ? catalog : []).forEach((p) => {
    if (!p || typeof p !== "object") return;
    const key = normSku(p.sku);
    if (key && !idx[key]) idx[key] = { sku: String(p.sku).trim(), name: p.name ? String(p.name) : "", case: toNum(p.case), price: toNum(p.price) || 0, isKit: !!p.isKit };
    (Array.isArray(p.variations) ? p.variations : []).forEach((v) => {
      const vk = normSku(v && v.sku);
      if (!vk || idx[vk]) return;
      const label = v.name || (p.name ? `${p.name}${v.label ? " - " + v.label : ""}` : v.label || "");
      idx[vk] = { sku: String(v.sku).trim(), name: String(label || ""), case: toNum(v.case != null ? v.case : p.case), price: toNum(v.price) || 0, isKit: false };
    });
  });
  return idx;
}

/** The ONE flattened product list used by BOTH the rep invoice picker and the
 *  rep "request stock" picker, so a SKU a rep can request is exactly the SKU he
 *  can invoice (ledger SKUs == invoice SKUs). A product that has variations is
 *  replaced by its variation lines (the parent itself is not pickable); a product
 *  without variations stays one entry. Output shape is what RepInvoices has always
 *  built, so invoice behaviour is unchanged. Pure; never throws. */
export function flattenCatalogForPicker(products) {
  return (Array.isArray(products) ? products : []).flatMap((p) => {
    if (!p || typeof p !== "object") return [];
    if (Array.isArray(p.variations) && p.variations.length > 0) {
      return p.variations.map((v) => ({
        sku: v.sku,
        name: `${p.name} — ${v.label || v.sku}`,
        price: Number(v.price) || 0,
        case: p.case,
        stock: Number(v.stock) || 0,
        category: p.category,
        imageUrl: p.imageUrl,
        parentSku: p.sku,
        variationLabel: v.label || "",
      }));
    }
    return [p];
  });
}

/** Per-SKU rows for ONE rep: the single source of received / sold / on-hand.
 *  Rows exist for every SKU present in the ledger OR in the rep's sold lines.
 *  @param {{movements:Array, invoices:Array, rep:object, catalog?:Array,
 *           since?:string|Date, types?:string[], anyRep?:boolean,
 *           resolver?:object, peers?:Array}} args
 *     resolver / peers: the whole rep list (createOwnerResolver or the identities) so
 *     a name-only stamp is resolved to ONE owner (admin); omit for the rep portal.
 *  @returns {Array<{sku,key,name,received,sold,soldInPeriod,onHand,oversold,oversoldBy,movementCount,byType,price,isKit}>}
 *     received = ledger sum; sold = ALL-TIME units; soldInPeriod = since-filtered
 *     (equals sold when no since); onHand = received - sold (signed, no clamp);
 *     oversold = onHand < 0; oversoldBy = max(0, -onHand). onHand NEVER uses the
 *     period. Sorted by sku. */
export function repInventoryRows(args) {
  const a = args || {};
  const cat = indexCatalog(a.catalog);
  const caseBySku = {};
  Object.keys(cat).forEach((k) => { if (cat[k].case != null) caseBySku[k] = cat[k].case; });
  // anyRep: true is for the admin "unknown rep id" bucket: every counted ledger row
  // of the given movements, no rep filter, and no invoices (a sale cannot be tied to
  // a rep id that matches no rep).
  const led = ledgerReceivedBySku(a.movements, { types: a.types, rep: a.anyRep ? undefined : (a.rep || {}) });
  const resolver = a.resolver || (Array.isArray(a.peers) ? createOwnerResolver(a.peers) : undefined);
  const sold = a.anyRep ? { bySku: {} } : soldBySku(a.invoices, a.rep, { since: a.since, caseBySku, resolver });
  const keys = new Set([...Object.keys(led.bySku), ...Object.keys(sold.bySku)]);
  const rows = [];
  keys.forEach((key) => {
    const l = led.bySku[key];
    const s = sold.bySku[key];
    const c = cat[key];
    const received = l ? l.received : 0;
    const soldUnits = s ? s.units : 0;
    const onHand = received - soldUnits;
    rows.push({
      sku: c ? c.sku : (l ? l.sku : s.sku),
      key,
      name: (c && c.name) || (l && l.name) || (s && s.name) || "",
      received,
      sold: soldUnits,
      soldInPeriod: s ? s.unitsInPeriod : 0,
      onHand,
      oversold: onHand < 0,
      oversoldBy: onHand < 0 ? -onHand : 0,
      movementCount: l ? l.count : 0,
      byType: l ? { ...l.byType } : {},
      price: c ? c.price : 0,
      isKit: c ? c.isKit : false,
    });
  });
  rows.sort((x, y) => (x.key < y.key ? -1 : x.key > y.key ? 1 : 0));
  return rows;
}

/** Totals over rows. Positive and negative on-hand are NEVER netted together:
 *  unitsOnHand counts only SKUs with stock, unitsOversold only the shortfalls.
 *  @returns {{unitsOnHand,unitsOversold,received,sold,skuCount,oversoldSkuCount}} */
export function inventoryTotals(rows) {
  const t = { unitsOnHand: 0, unitsOversold: 0, received: 0, sold: 0, skuCount: 0, oversoldSkuCount: 0 };
  (Array.isArray(rows) ? rows : []).forEach((r) => {
    if (!r) return;
    const oh = Number(r.onHand) || 0;
    t.unitsOnHand += Math.max(0, oh);
    t.unitsOversold += Math.max(0, -oh);
    t.received += Number(r.received) || 0;
    t.sold += Number(r.sold) || 0;
    t.skuCount += 1;
    if (oh < 0) t.oversoldSkuCount += 1;
  });
  return t;
}

/** Compare two row sets (rep screen vs admin screen) field by field.
 *  @returns {{equal:boolean, diffs:Array<{key,field,a,b}>}} */
export function compareInventories(a, b) {
  const FIELDS = ["received", "sold", "onHand"];
  const ma = {}; (Array.isArray(a) ? a : []).forEach((r) => { if (r) ma[r.key] = r; });
  const mb = {}; (Array.isArray(b) ? b : []).forEach((r) => { if (r) mb[r.key] = r; });
  const diffs = [];
  Array.from(new Set([...Object.keys(ma), ...Object.keys(mb)])).sort().forEach((key) => {
    FIELDS.forEach((f) => {
      const va = ma[key] ? ma[key][f] : undefined;
      const vb = mb[key] ? mb[key][f] : undefined;
      if (va !== vb) diffs.push({ key, field: f, a: va, b: vb });
    });
  });
  return { equal: diffs.length === 0, diffs };
}

// ── rep list overlay + fresh invoice baseline ──────────────────────────────────

/** Overlay SHARED_REP_PROFILES onto a rep list the way the Rep Inventory tab always
 *  has (an admin edit of a rep's display name / email flows into the list), returning
 *  NEW objects (the input is not mutated). Reps are { key, name, email, ... }. */
export function overlayRepProfiles(reps, sharedRepProfiles) {
  const out = (Array.isArray(reps) ? reps : []).map((r) => ({ ...r }));
  Object.values(sharedRepProfiles || {}).forEach((sp) => {
    if (!sp || !sp.name) return;
    const match = out.find((r) => r.name === sp.name || (r.email && sp.email && String(r.email).toLowerCase() === String(sp.email).toLowerCase()));
    if (!match) return;
    if (sp.email && !match.email) match.email = sp.email;
    if (sp.name !== "—" && match.name !== sp.name) match.name = sp.name;
  });
  return out;
}

/** A minimal invoice (what inventoryMath reads) from a portal_orders row, with the
 *  same id rule the cache hydration uses (invoice_ref, invoice_id, order_ref,
 *  order_id, then the row id). items may be a JSON string (TEXT column) and may be
 *  double-encoded; unparseable items give null so a merge keeps the cached lines.
 *  Returns null for a row without any id. */
export function invoiceFromOrderRow(row) {
  if (!row || typeof row !== "object") return null;
  const rawId = row.invoice_ref || row.invoice_id || row.order_ref || row.order_id || (row.id != null ? row.id : "");
  if (rawId === "" || rawId == null) return null;
  let items = row.items;
  for (let i = 0; i < 2 && typeof items === "string"; i++) {
    try { items = JSON.parse(items); } catch (e) { items = null; }
  }
  const rawOrderId = row.order_ref || row.order_id || (row.id != null ? row.id : "");
  return {
    id: String(rawId),
    orderId: rawOrderId === "" || rawOrderId == null ? "" : String(rawOrderId),
    rowId: row.id != null && row.id !== "" ? String(row.id) : "",
    customer: row.customer ? String(row.customer) : "",
    items: Array.isArray(items) ? items : null,
    // A NULL / empty portal_orders.status is kept EMPTY here (it means "the server never
    // said"), so a merge can never read it as a re-open of a cancelled cache copy.
    status: row.status ? String(row.status) : "",
    rep: row.rep ? String(row.rep) : "",
    source: row.source ? String(row.source) : "",
    deliveryMethod: row.delivery_method ? String(row.delivery_method) : "",
    deliveredByRep: row.delivered_by_rep ? String(row.delivered_by_rep) : "",
    createdAt: row.created_at ? String(row.created_at) : "",
    fromDb: true,
  };
}

/** The status of a merged invoice. Cancellation is TERMINAL: if EITHER copy is
 *  cancelled / canceled / removed the merged invoice is cancelled (the repo itself says
 *  portal_orders.status can be stale or NULL: a void made on an older portal version may
 *  not be saved yet, and the cancel patches are forward-only), a database cancel wins
 *  over anything, and an empty / NULL database status never overrides the cache's. */
export function mergedInvoiceStatus(cacheStatus, dbStatus) {
  if (NON_DEDUCTING_STATUSES.has(low(dbStatus))) return String(dbStatus);
  if (NON_DEDUCTING_STATUSES.has(low(cacheStatus))) return String(cacheStatus);
  return dbStatus ? String(dbStatus) : (cacheStatus == null ? "" : String(cacheStatus));
}

/** The keys cache hydration matches a portal_orders row by (App.js hydrates a row only
 *  when none of invoice id, orderId or String(row.id) is already cached): the invoice id
 *  rule id, the order id rule, and the row id. */
function dbKeysOf(d) {
  return [d.id, d.orderId, d.rowId].filter((k) => k != null && k !== "");
}

/** Merge FRESH portal_orders rows over the cached invoices for a baseline that must not
 *  depend on this browser's cache (the admin Adjust modal). A database row is matched to
 *  a cached invoice by the SAME keys the cache hydration uses (invoice id, orderId,
 *  String(row.id)), so a cached invoice is never duplicated as a second database-only
 *  copy. The database wins for items (when it has any) and for rep name / source /
 *  delivery fields (when set). Status: cancellation is terminal (see
 *  mergedInvoiceStatus): a stale or NULL database status can never revive an invoice
 *  that is cancelled in the cache, and a database cancel always wins. Fields only the
 *  cache knows (repId, repEmail, repCreated, ...) are kept. A cached invoice the
 *  database does not have stays as it is (an unsynced local invoice still counts); a
 *  database-only invoice is added. Returns a new array; neither input is mutated. */
export function mergeFreshInvoices(cacheInvoices, dbRows) {
  const out = dedupeInvoicesById(cacheInvoices).slice();
  const at = new Map();
  const register = (inv, i) => {
    [inv.id, inv.orderId].forEach((k) => { if (k != null && k !== "" && !at.has(String(k))) at.set(String(k), i); });
  };
  out.forEach((inv, i) => { if (inv && typeof inv === "object") register(inv, i); });
  (Array.isArray(dbRows) ? dbRows : []).forEach((row) => {
    const d = invoiceFromOrderRow(row);
    if (!d) return;
    const keys = dbKeysOf(d);
    let i = null;
    for (let k = 0; k < keys.length && i == null; k++) { if (at.has(String(keys[k]))) i = at.get(String(keys[k])); }
    if (i == null) {
      const added = { ...d, status: d.status || "outstanding" };
      keys.forEach((k) => { if (!at.has(String(k))) at.set(String(k), out.length); });
      out.push(added);
      return;
    }
    const c = out[i];
    out[i] = {
      ...c,
      status: mergedInvoiceStatus(c.status, d.status),
      items: d.items && d.items.length > 0 ? d.items : c.items,
      rep: d.rep || c.rep,
      source: d.source || c.source,
      customer: d.customer || c.customer,
      deliveryMethod: d.deliveryMethod || c.deliveryMethod,
      deliveredByRep: d.deliveredByRep || c.deliveredByRep,
    };
  });
  return out;
}

/** Which invoices make the FRESH baseline differ from the cache, for the Adjust modal's
 *  "why": every merged invoice whose counted units of `opts.sku` differ from the cached
 *  copy (a server cancel the cache has not seen, changed lines, or an invoice only the
 *  server has). Counted units are what the stock rule would take from a rep (0 for a
 *  cancelled invoice or one that cannot deduct). `opts.owns(inv)` (optional) keeps only
 *  invoices the target rep owns in either copy; `opts.caseBySku` as for lineUnits.
 *  @param {Array} cacheInvoices  the invoices as cached in this browser
 *  @param {Array} mergedInvoices mergeFreshInvoices(cacheInvoices, rows)
 *  @param {{sku?:string, owns?:(inv)=>boolean, caseBySku?:Object}} [opts]
 *  @returns {Array<{id:string, kind:"new"|"changed", cacheStatus:string, freshStatus:string,
 *                   cacheUnits:number, freshUnits:number}>} */
export function freshInvoiceChanges(cacheInvoices, mergedInvoices, opts) {
  const o = opts || {};
  const wantKey = o.sku != null ? normSku(o.sku) : null;
  const unitsOf = (inv) => {
    if (!inv || !invoiceCanDeductStock(inv)) return 0;
    return invoiceItems(inv).reduce((s, it) => (wantKey == null || normSku(it.sku) === wantKey ? s + lineUnits(it, inv, o) : s), 0);
  };
  const cache = new Map();
  dedupeInvoicesById(cacheInvoices).forEach((c) => {
    [c.id, c.orderId].forEach((k) => { if (k != null && k !== "" && !cache.has(String(k))) cache.set(String(k), c); });
  });
  const out = [];
  (Array.isArray(mergedInvoices) ? mergedInvoices : []).forEach((m) => {
    if (!m || typeof m !== "object") return;
    const c = [m.id, m.orderId].map((k) => (k == null || k === "" ? null : cache.get(String(k)))).find(Boolean) || null;
    const cacheUnits = unitsOf(c);
    const freshUnits = unitsOf(m);
    if (cacheUnits === freshUnits) return;
    if (typeof o.owns === "function" && !(o.owns(m) || (c && o.owns(c)))) return;
    out.push({
      id: m.id == null ? "" : String(m.id),
      kind: c ? "changed" : "new",
      cacheStatus: c ? String(c.status || "") : "",
      freshStatus: String(m.status || ""),
      cacheUnits,
      freshUnits,
    });
  });
  return out;
}
